import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import MasterSearch from "@/components/MasterSearch";
import VenueCard, { VenueCardSkeleton } from "@/components/VenueCard";
import { Star, ArrowRight, Play, Sparkles, ShieldCheck, Award, Heart, Palette, Utensils, Home as HomeIcon, Building2, Crown, Search as SearchIcon, MessageCircle, CalendarCheck } from "lucide-react";

const HERO_IMAGES = [
  "https://images.unsplash.com/photo-1761472606347-bfebc5a3e546?crop=entropy&cs=srgb&fm=jpg&w=1800",
  "https://images.unsplash.com/photo-1708606811579-23b18fc48007?crop=entropy&cs=srgb&fm=jpg&w=1800",
];

export default function Home() {
  const [cities, setCities] = useState([]);
  const [activeCity, setActiveCity] = useState("Belgaum");
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testimonials, setTestimonials] = useState([]);
  const [premium, setPremium] = useState([]);

  useEffect(() => {
    api.get("/cities").then((r)=>setCities(r.data)).catch(()=>{});
    api.get("/testimonials").then((r)=>setTestimonials(r.data)).catch(()=>{});
  }, []);

  useEffect(() => {
    if (!activeCity) return;
    setLoading(true);
    api.get(`/venues/highlighted/${encodeURIComponent(activeCity)}`).then((r)=>{
      setFeatured(r.data.slice(0,6));
      setPremium(r.data.slice(0,10));
    }).catch(()=>{}).finally(()=>setLoading(false));
  }, [activeCity]);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMAGES[0]} className="w-full h-full object-cover" alt="Grand Indian wedding hall"/>
          <div className="absolute inset-0 hero-overlay"/>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-24 w-full text-white">
          <motion.div initial={{opacity:0, y:20}} animate={{opacity:1, y:0}} transition={{duration:0.8}} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest-2 text-[color:var(--gold-2)] mb-5">
              <Sparkles className="w-3 h-3"/> India's grandest wedding marketplace
            </span>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95]">
              Where every <em className="not-italic text-[color:var(--gold-2)]">Shubh Muhurat</em> finds its perfect stage.
            </h1>
            <p className="mt-6 text-base md:text-lg text-white/85 max-w-2xl">
              Discover regal banquet halls, sprawling lawns, elite catering & signature decor across 25+ Indian states. Curated · Verified · Instantly bookable.
            </p>
          </motion.div>
          <motion.div initial={{opacity:0, y:20}} animate={{opacity:1, y:0}} transition={{delay:0.2, duration:0.8}} className="mt-10 max-w-5xl">
            <MasterSearch/>
          </motion.div>

          <div className="mt-10 flex flex-wrap gap-6 text-xs text-white/70">
            <Stat icon={<ShieldCheck className="w-4 h-4"/>} label="Verified Owners" value="2,400+"/>
            <Stat icon={<Award className="w-4 h-4"/>} label="Premium Venues" value="820+"/>
            <Stat icon={<Heart className="w-4 h-4"/>} label="Happy Weddings" value="18,000+"/>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20" data-testid="categories-section">
        <div className="text-[10px] uppercase tracking-widest-2 text-[color:var(--wine)]">Everything under one mandap</div>
        <h2 className="font-display text-4xl md:text-5xl mt-1 mb-12">Discover by category</h2>
        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
          {[
            {k:"banquet_hall", l:"Banquet Halls", i: Building2, img:"https://images.pexels.com/photos/33852468/pexels-photo-33852468.jpeg?auto=compress&cs=tinysrgb&h=650&w=940"},
            {k:"lawn", l:"Wedding Lawns", i: HomeIcon, img:"https://images.pexels.com/photos/37331160/pexels-photo-37331160.jpeg?auto=compress&cs=tinysrgb&h=650&w=940"},
            {k:"catering", l:"Catering", i: Utensils, img:"https://images.pexels.com/photos/36766881/pexels-photo-36766881.jpeg?auto=compress&cs=tinysrgb&h=650&w=940"},
            {k:"hotel_banquet", l:"Hotel Banquets", i: Crown, img:"https://images.unsplash.com/photo-1708606811579-23b18fc48007?crop=entropy&cs=srgb&fm=jpg&w=940"},
            {k:"decoration", l:"Decoration", i: Palette, img:"https://images.pexels.com/photos/12688995/pexels-photo-12688995.jpeg?auto=compress&cs=tinysrgb&h=650&w=940"},
          ].map((c, i)=>(
            <motion.div key={c.k} initial={{opacity:0, y:20}} whileInView={{opacity:1, y:0}} viewport={{once:true}} transition={{delay: i*0.08}}>
              <Link to={`/properties?category=${c.k}`} className="group block relative rounded-2xl overflow-hidden aspect-[3/4]" data-testid={`cat-${c.k}`}>
                <img src={c.img} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"/>
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent"/>
                <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                  <c.i className="w-5 h-5 text-[color:var(--gold-2)] mb-2"/>
                  <div className="font-display text-2xl">{c.l}</div>
                  <div className="text-xs text-white/70 mt-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">Explore <ArrowRight className="w-3 h-3"/></div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-[color:var(--ivory-2)] py-20" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-[10px] uppercase tracking-widest-2 text-[color:var(--wine)]">How it works</div>
          <h2 className="font-display text-4xl md:text-5xl mt-1 mb-12">Book your perfect venue in three effortless steps</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {n:"01", i: SearchIcon, t:"Discover", d:"Browse hand-picked venues, lawns, caterers & decor across 180+ Indian cities."},
              {n:"02", i: MessageCircle, t:"Connect", d:"Send an inquiry with your date, guest count & wishlist. Owners reply within hours."},
              {n:"03", i: CalendarCheck, t:"Celebrate", d:"Lock your date, pay directly with the venue, and let the countdown to your Shubh Muhurat begin."},
            ].map((s, i)=>(
              <motion.div key={s.n} initial={{opacity:0, y:20}} whileInView={{opacity:1, y:0}} viewport={{once:true}} transition={{delay: i*0.15}} className="card-luxury p-8 relative">
                <div className="absolute -top-4 -right-4 w-14 h-14 rounded-full bg-[color:var(--wine)] text-[color:var(--gold-2)] flex items-center justify-center font-display text-xl shadow-lg">{s.n}</div>
                <s.i className="w-8 h-8 text-[color:var(--wine)] mb-4"/>
                <div className="font-display text-2xl mb-2">{s.t}</div>
                <p className="text-sm text-neutral-600 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* City selector + 6 highlighted */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20" data-testid="featured-section">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest-2 text-[color:var(--wine)]">Handpicked for you</div>
            <h2 className="font-display text-4xl md:text-5xl mt-1">Six standout venues in <span className="text-[color:var(--wine)]">{activeCity}</span></h2>
          </div>
          <div className="flex items-center gap-3">
            <select value={activeCity} onChange={(e)=>setActiveCity(e.target.value)} className="px-4 py-2.5 rounded-full bg-white border border-[color:var(--line)] text-sm" data-testid="home-city-select">
              {cities.map((c)=><option key={c.name}>{c.name}</option>)}
            </select>
            <Link to={`/properties?city=${encodeURIComponent(activeCity)}`} className="text-sm text-[color:var(--wine)] hover:underline inline-flex items-center gap-1">All in {activeCity} <ArrowRight className="w-4 h-4"/></Link>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading && Array.from({length:6}).map((_,i)=><VenueCardSkeleton key={i}/>)}
          {!loading && featured.length === 0 && (
            <div className="col-span-3 text-center py-16 text-neutral-500">
              No highlighted venues in {activeCity} yet. <Link className="text-[color:var(--wine)] underline" to={`/properties?city=${activeCity}`}>Browse all →</Link>
            </div>
          )}
          {!loading && featured.map((v)=><VenueCard key={v.id} v={v}/>) }
        </div>
      </section>

      {/* Premium auto-slider */}
      {premium.length > 0 && (
        <section className="py-16 bg-[color:var(--ivory-2)] overflow-hidden" data-testid="premium-carousel">
          <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-end justify-between mb-8">
            <div>
              <div className="text-[10px] uppercase tracking-widest-2 text-[color:var(--wine)]">Premium Spotlight · {activeCity}</div>
              <h2 className="font-display text-3xl md:text-4xl mt-1">Ten venues booked by discerning families</h2>
            </div>
          </div>
          <div className="overflow-hidden">
            <div className="marquee px-6">
              {[...premium, ...premium].map((v, i)=>(
                <Link to={`/property/${v.id}`} key={i} className="min-w-[260px] max-w-[260px] card-luxury overflow-hidden block">
                  <img src={v.images?.[0]} className="h-40 w-full object-cover" alt={v.name}/>
                  <div className="p-4">
                    <div className="font-display text-lg leading-tight">{v.name}</div>
                    <div className="text-xs text-neutral-600 mt-1">{v.city} · up to {v.pax_max} guests</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Popular cities grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20" data-testid="popular-cities-section">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest-2 text-[color:var(--wine)]">Where India weds</div>
            <h2 className="font-display text-4xl md:text-5xl mt-1">Popular wedding cities</h2>
          </div>
          <Link to="/highlighted" className="text-sm text-[color:var(--wine)] hover:underline inline-flex items-center gap-1">View all curated <ArrowRight className="w-4 h-4"/></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            {n:"Belgaum", img:"https://images.pexels.com/photos/37331160/pexels-photo-37331160.jpeg?auto=compress&cs=tinysrgb&h=650&w=940"},
            {n:"Bengaluru", img:"https://images.pexels.com/photos/33852468/pexels-photo-33852468.jpeg?auto=compress&cs=tinysrgb&h=650&w=940"},
            {n:"Mumbai", img:"https://images.unsplash.com/photo-1607861884586-c7cfaed16290?crop=entropy&cs=srgb&fm=jpg&w=940"},
            {n:"Jaipur", img:"https://images.unsplash.com/photo-1616668856493-9df876327739?w=940"},
            {n:"Delhi", img:"https://images.unsplash.com/photo-1708606811579-23b18fc48007?crop=entropy&cs=srgb&fm=jpg&w=940"},
            {n:"Hyderabad", img:"https://images.pexels.com/photos/12688995/pexels-photo-12688995.jpeg?auto=compress&cs=tinysrgb&h=650&w=940"},
            {n:"Chennai", img:"https://images.pexels.com/photos/36766881/pexels-photo-36766881.jpeg?auto=compress&cs=tinysrgb&h=650&w=940"},
            {n:"Kolkata", img:"https://images.pexels.com/photos/35839965/pexels-photo-35839965.jpeg?auto=compress&cs=tinysrgb&h=650&w=940"},
          ].map((c, i)=>(
            <motion.div key={c.n} initial={{opacity:0, scale:0.95}} whileInView={{opacity:1, scale:1}} viewport={{once:true}} transition={{delay: i*0.05}}>
              <Link to={`/properties?city=${encodeURIComponent(c.n)}`} className="group block relative rounded-2xl overflow-hidden aspect-[4/5]" data-testid={`city-${c.n}`}>
                <img src={c.img} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"/>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"/>
                <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                  <div className="font-display text-2xl">{c.n}</div>
                  <div className="text-[10px] tracking-widest-2 uppercase text-[color:var(--gold-2)] opacity-0 group-hover:opacity-100 transition-opacity mt-1">Explore venues →</div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Video showcase */}
      <section className="relative py-24 overflow-hidden" data-testid="videos-section">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1616668856493-9df876327739?w=1800')] bg-cover bg-center opacity-10"/>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="text-[10px] uppercase tracking-widest-2 text-[color:var(--wine)]">Reel weddings</div>
              <h2 className="font-display text-4xl md:text-5xl mt-1">Films that made India smile</h2>
            </div>
            <div className="hidden md:flex items-center gap-2 text-[color:var(--wine)] text-sm">
              <Play className="w-4 h-4"/> Cinematic recaps · 60-second reels
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {yt:"2Vv-BfVoq4g", poster:"https://images.pexels.com/photos/37331160/pexels-photo-37331160.jpeg?auto=compress&cs=tinysrgb&h=650&w=940", title:"Shagun Lawns · Belgaum"},
              {yt:"2Vv-BfVoq4g", poster:"https://images.pexels.com/photos/33852468/pexels-photo-33852468.jpeg?auto=compress&cs=tinysrgb&h=650&w=940", title:"Grand Palace · Bengaluru"},
              {yt:"2Vv-BfVoq4g", poster:"https://images.pexels.com/photos/12688995/pexels-photo-12688995.jpeg?auto=compress&cs=tinysrgb&h=650&w=940", title:"Royal Mandap · Jaipur"},
            ].map((v, i)=>(
              <VideoCard key={i} {...v}/>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-[#1a0a0f] text-white py-20" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-[10px] uppercase tracking-widest-2 text-[color:var(--gold-2)]">Reviews & Testimonials</div>
          <h2 className="font-display text-4xl md:text-5xl mt-1 mb-12">Loved by 18,000+ families</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i)=>(
              <div key={i} className="p-6 rounded-2xl bg-white/5 backdrop-blur border border-white/10">
                <div className="flex gap-1 text-[color:var(--gold-2)] mb-4">
                  {Array.from({length: t.rating||5}).map((_,i)=><Star key={i} className="w-4 h-4 fill-current"/>)}
                </div>
                <p className="font-display text-2xl leading-snug">"{t.text}"</p>
                <div className="mt-5 flex items-center gap-3">
                  {t.avatar && <img src={t.avatar} className="w-10 h-10 rounded-full object-cover" alt={t.name}/>}
                  <div>
                    <div className="text-sm font-medium">{t.name}</div>
                    <div className="text-xs text-neutral-400">{t.city}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA - Grand banner */}
      <section className="relative py-24 overflow-hidden" data-testid="owner-cta-section">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/12688995/pexels-photo-12688995.jpeg?auto=compress&cs=tinysrgb&h=1200&w=1800" className="w-full h-full object-cover"/>
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a0a0f]/95 via-[#1a0a0f]/70 to-transparent"/>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-10 items-center text-white">
          <div>
            <div className="text-[10px] uppercase tracking-widest-2 text-[color:var(--gold-2)]">For Venue Owners</div>
            <h2 className="font-display text-4xl md:text-6xl mt-2 leading-[0.95]">Turn your lawn or banquet into a destination.</h2>
            <p className="mt-5 text-white/85 max-w-md leading-relaxed">List once, get discovered by thousands of couples across India. Manage packages, availability & premium spotlight slots — from a beautiful owner dashboard.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" className="btn-wine">List your venue</Link>
              <Link to="/pricing" className="btn-ghost !text-white !border-white/40 hover:!bg-white hover:!text-[color:var(--wine)]">See pricing</Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-xs text-white/70">
              <span>✓ 6-month subscriptions</span>
              <span>✓ Multi-city premium slots</span>
              <span>✓ Instant inquiry alerts</span>
            </div>
          </div>
        </div>
      </section>

      {/* Elegant closing quote */}
      <section className="max-w-4xl mx-auto px-6 py-20 text-center">
        <div className="text-[10px] uppercase tracking-widest-2 text-[color:var(--wine)] mb-4">A promise</div>
        <p className="font-display text-3xl md:text-4xl leading-tight text-neutral-800">
          "Every wedding deserves its <em className="not-italic text-[color:var(--wine)]">Shubh Muhurat</em> — and every Shubh Muhurat, its perfect stage."
        </p>
        <div className="mt-6 text-sm text-neutral-500">— The Shubh Muhurat team</div>
      </section>
    </div>
  );
}

function Stat({icon, label, value}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[color:var(--gold-2)]">{icon}</span>
      <span className="font-medium text-white">{value}</span>
      <span className="text-white/60">{label}</span>
    </div>
  );
}

function VideoCard({ yt, poster, title }) {
  const [playing, setPlaying] = useState(false);
  return (
    <div className="aspect-video rounded-2xl overflow-hidden card-luxury relative group">
      {playing ? (
        <iframe src={`https://www.youtube.com/embed/${yt}?autoplay=1`} title={title} className="w-full h-full" allowFullScreen/>
      ) : (
        <button onClick={()=>setPlaying(true)} className="w-full h-full relative block">
          <img src={poster} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
          <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors"/>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
              <Play className="w-6 h-6 text-[color:var(--wine)] fill-current ml-1"/>
            </div>
          </div>
          <div className="absolute inset-x-0 bottom-0 p-4 text-white text-left">
            <div className="text-[10px] tracking-widest-2 uppercase text-[color:var(--gold-2)]">Real wedding</div>
            <div className="font-display text-xl">{title}</div>
          </div>
        </button>
      )}
    </div>
  );
}
